import React, { useEffect, useMemo, useState } from "react";
import { useCommentBank } from "../../hooks/useGeneralAssessment";
import Button from "../shared/Button";
import { Spinner } from "reactstrap";
import Select from "../shared/Select";
import { useSubjects } from "../../hooks/useSubjects";
import swal from "sweetalert";
import CustomModal from "../shared/CustomModal";
import { fas } from "@fortawesome/free-solid-svg-icons";

export const badges = [
  { value: "First Name" },
  { value: "Middle Name" },
  { value: "Last Name" },
  { value: "He/She" },
  { value: "he/she" },
  { value: "His/Her" },
  { value: "his/her" },
  { value: "his/hers" },
  { value: "him/her" },
];

export const sharingOptions = [
  {
    label: "Public",
    value: true,
  },
  {
    label: "Private",
    value: false,
  },
];

export const templateOptions = [
  {
    label: "My Templates",
    value: true,
  },
  {
    label: "Public Templates",
    value: false,
  },
];

const CommentBankModal = ({
  tagsValue,
  setCommentsConfig,
  closeModal,
  selectedCommentId,
  setSelectedCommentId,
}) => {
  const {
    commentBank,
    loading,
    getCommentBank,
    createCommentTemplate,
    deleteCommentTemplate,
  } = useCommentBank();
  const { subjects } = useSubjects();
  const [formData, setFormData] = useState({
    commentText: "",
    isPublic: null,
  });
  const [assessment, setAssessment] = useState(null);
  const [isMyTemplates, setIsMyTemplates] = useState(true);
  const [deleteModal, setDeleteModal] = useState(false);
  const [currentComment, setCurrentComment] = useState(null);

  //subject options
  const assessmentOptions = useMemo(() => {
    if (subjects?.length === 0) return [];

    const subjectsList = subjects?.map((item) => ({
      label: item?.name,
      value: item?.id,
    }));

    return [
      { label: "General Assessment", value: "generalAssessment" },
      {
        label: "Subject Assessment",
        options: subjectsList,
      },
    ];
  });

  //tags click handler
  const handleTagsClick = (e) => {
    const { value } = e.target;

    setFormData((prev) => ({
      ...prev,
      commentText: prev.commentText
        ? prev.commentText + ` [${value}]`
        : `[${value}]`,
    }));
  };

  const handleFormDataChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCreateCommentTemplate = () => {
    if (formData?.commentText?.trim() === "") {
      swal("Oops", "Comment Text is requried");
      return;
    }

    if (!assessment) {
      swal("Oops", "Assessment Type is required");
      return;
    }

    if (formData?.isPublic === null) {
      swal("Oops", "Sharing Option is requried");
      return;
    }

    const params = {
      commentText: formData?.commentText,
      isPublic: formData?.isPublic,
      isGeneralAssessment: assessment?.value === "generalAssessment",
      subjectId:
        assessment?.value !== "generalAssessment" ? assessment?.value : "",
    };

    createCommentTemplate(params);

    //reset states
    setFormData({
      commentText: "",
      isPublic: null,
    });
    setAssessment(null);
    setIsMyTemplates(true);
  };

  const handleTemplateInsert = (item) => {
    const commentText = item?.commentText;
    let result = commentText.replace(/\[([^\]]+)\]/g, (match, key) => {
      return tagsValue[key] ?? match;
    });

    setCommentsConfig((prev) => {
      const updated = prev?.map((comment) => {
        if (
          comment?.assessmentGeneralCommentConfigurationId !== selectedCommentId
        )
          return comment;

        return {
          ...comment,
          commentTextValue: result,
        };
      });

      return updated;
    });

    setSelectedCommentId(null);
    closeModal();
  };

  //set first item as select value
  useEffect(() => {
    setIsMyTemplates(templateOptions[0]?.value);
  }, []);

  return (
    <>
      <div className="arr-form">
        <div>
          <textarea
            value={formData?.commentText}
            onChange={(e) =>
              handleFormDataChange("commentText", e.target.value)
            }
            className="form-control"
            name="commentText"
            placeholder="Enter your comment template"
          />
          <div
            style={{ gap: "6px", flexWrap: "wrap", marginTop: "10px" }}
            className="flex"
          >
            {badges?.map((item) => (
              <input
                key={item?.value}
                style={{ border: "none" }}
                className="arr-badge"
                type="button"
                value={item?.value}
                onClick={handleTagsClick}
              />
            ))}
          </div>
        </div>

        <Select
          options={assessmentOptions}
          value={assessment ?? null}
          onChange={(option) => setAssessment(option)}
          isClearable={false}
          styles={{ control: { width: "250px" } }}
          placeholder={"Select Assessment Type"}
        />

        <Select
          options={sharingOptions}
          value={
            sharingOptions?.find((opt) => opt?.value === formData?.isPublic) ||
            null
          }
          onChange={(option) => handleFormDataChange("isPublic", option?.value)}
          isClearable={false}
          styles={{ control: { width: "200px" } }}
          placeholder={"Select Sharing Option"}
        />
        <Button
          style={{ height: "fit-content" }}
          disabled={loading?.create}
          onClick={handleCreateCommentTemplate}
        >
          {loading?.create ? <Spinner size={"sm"} color="white" /> : "Create"}
        </Button>
      </div>

      <div style={{ marginTop: "16px" }} className="action-btns end">
        <Select
          options={templateOptions}
          value={
            templateOptions?.find((opt) => opt.value === isMyTemplates) || null
          }
          onChange={(option) => {
            if (isMyTemplates === option?.value) return;
            setIsMyTemplates(option?.value);
            getCommentBank(option?.value);
          }}
          isClearable={false}
          styles={{ control: { width: "200px" } }}
        />
      </div>

      <div className="arr-table-container">
        <table className="arr-table">
          <thead>
            <tr>
              <th>Comments</th>
              <th>Assessment Type</th>
              <th>Sharing Options</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading.read ? (
              <tr>
                <td colSpan={4}>
                  <div className="arr-spinner">
                    <div className="loader">
                      <Spinner size={"md"} color="primary"></Spinner>
                    </div>
                    <div style={{ fontSize: "13px", marginTop: "15px" }}>
                      Please wait !! Processing.....
                    </div>
                  </div>
                </td>
              </tr>
            ) : commentBank?.length > 0 ? (
              commentBank?.map((item) => (
                <tr key={item?.id}>
                  <td>{item?.commentText}</td>
                  <td>
                    {item?.isGeneralAssessment === true
                      ? "General Assessment"
                      : item?.subjectName}
                  </td>
                  <td>{item?.isPublic === true ? "Public" : "Private"}</td>
                  <td>
                    <div className="action-btns">
                      <Button
                        onClick={() => handleTemplateInsert(item)}
                        size="sm"
                      >
                        Insert
                      </Button>
                      {isMyTemplates && (
                        <>
                          <Button size="sm" variant="outline">
                            Edit
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => {
                              setCurrentComment(item);
                              setDeleteModal(true);
                            }}
                          >
                            Delete
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td style={{ textAlign: "center" }} colSpan={4}>
                  No Data Found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* delete modal */}
      <CustomModal
        isOpen={deleteModal}
        toggle={() => setDeleteModal(false)}
        modalHeader={"Are you sure?"}
        children={
          <div>
            <p>Once Deleted, you will not be able to recover this record</p>
            <div className="action-btns end">
              <Button variant="outline" onClick={() => setDeleteModal(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                disabled={loading.delete}
                onClick={() => {
                  deleteCommentTemplate(currentComment?.id);
                  setCurrentComment(null);
                  setDeleteModal(false);
                }}
              >
                {loading.delete ? (
                  <Spinner size={"sm"} color="white" />
                ) : (
                  "Delete"
                )}
              </Button>
            </div>
          </div>
        }
      />
    </>
  );
};

export default CommentBankModal;
